import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Empty, List, Space, Tag } from "antd";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { message } from "../../components/Message";
import { canVisitModule, toIdentityTypeNumber } from "../../utils/permission";
import { getDistributeIdentityLabel } from "../../utils/workspace";
import { isMobileDevice } from "../../utils/util";
import styles from "./index.modules.less";

const buildIdentityKey = (item = {}) => `${item.identityType}_${item.identityId}`;

const IdentitySelect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const permission = useSelector((state) => state.permission);
  const [selectedKey, setSelectedKey] = useState("");

  useEffect(() => {
    const currentIdentity = permission.currentIdentity;
    if (currentIdentity?.identityType && currentIdentity?.identityId) {
      setSelectedKey(buildIdentityKey(currentIdentity));
    }
  }, [permission.currentIdentity?.identityType, permission.currentIdentity?.identityId]);

  const selectedIdentity = useMemo(() => {
    return permission.identityList.find((item) => buildIdentityKey(item) === selectedKey) || null;
  }, [permission.identityList, selectedKey]);

  const handleEnter = async () => {
    if (!selectedIdentity) {
      message.show({
        type: "error",
        content: "请选择一个身份后继续",
      });
      return;
    }

    try {
      const data = await dispatch.permission.switchIdentityAsync({
        identity_type: toIdentityTypeNumber(selectedIdentity.identityType),
        identity_id: selectedIdentity.identityId,
      });
      if (data?.needSelectIdentity) {
        message.show({
          type: "error",
          content: "身份切换未生效，请刷新后重试或联系管理员",
        });
        return;
      }
      const isMobile = isMobileDevice() || window.innerWidth <= 768;
      const canOpenComplaint = canVisitModule({
        identityType: data.identityType,
        permissionCodes: data.permissionCodes,
        requiredPermission: "complaint_handling",
      });
      const target = isMobile && canOpenComplaint
        ? "/view/complaint_center"
        : (data.landingPath || "/view/home");
      navigate(target, { replace: true });
    } catch (error) {
      message.show({
        type: "error",
        content: error?.errmsg || "身份切换失败，请稍后重试",
      });
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const pageMode = location.state?.source === "switch" || permission.identitySelectionMode === "switch" ? "switch" : "initial";
  const pageTitle = pageMode === "switch" ? "切换当前厂商身份" : "请选择当前要进入的厂商身份";
  const pageDescription =
    pageMode === "switch"
      ? "已为你高亮当前使用中的身份，点击其他身份后可切换站内权限与页面。"
      : "检测到当前账号存在多个可用身份，请先选择本次登录要进入的身份。";

  const hasIdentities = Array.isArray(permission.identityList) && permission.identityList.length > 0;

  return (
    <div className={styles.page}>
      <Card title={pageTitle}>
        <div className={styles.description}>{pageDescription}</div>
        {hasIdentities ? (
          <List
            split={false}
            dataSource={permission.identityList}
            renderItem={(item) => {
              const identityKey = buildIdentityKey(item);
              const checked = selectedKey === identityKey;
              const isCurrent = Boolean(item.isCurrent);
              return (
                <List.Item
                  className={classNames(styles.identityItem, {
                    [styles.identityItemActive]: checked,
                    [styles.identityItemCurrent]: isCurrent,
                  })}
                  onClick={() => setSelectedKey(identityKey)}
                >
                  <div className={styles.identityContent}>
                    <div className={styles.vendorName}>{item.vendorName || "--"}</div>
                    <div className={styles.meta}>
                      <Tag color={item.identityType === "owner" ? "gold" : "blue"}>
                        {item.identityType === "owner" ? "主账号" : "子账号"}
                      </Tag>
                      <Tag>{getDistributeIdentityLabel(item.distributeIdentity)}</Tag>
                      {isCurrent && <Tag color="processing">当前身份</Tag>}
                      <span className={styles.accountDisplay}>{item.accountDisplay || "--"}</span>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        ) : (
          <Empty description="暂无可用身份，请联系管理员处理" />
        )}
        <div className={styles.footer}>
          <Space>
            {pageMode === "switch" && (
              <Button onClick={handleCancel}>取消</Button>
            )}
            <Button
              type="primary"
              loading={permission.identitySwitching}
              onClick={handleEnter}
              disabled={!hasIdentities}
            >
              {pageMode === "switch" ? "确认切换" : "进入"}
            </Button>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default IdentitySelect;
